/**
 * Live-match snapshot persistence.
 *
 * The in-match state (players, scores, goals, timer intervals) lives in
 * React state, so a refresh mid-match used to wipe everything. This module
 * snapshots that state to localStorage on every change and restores it on
 * boot. It works because playtime is derived from wall-clock timestamps
 * (playIntervals / gameIntervals), so a restored snapshot keeps the clock
 * and all playtimes exactly right — including a running timer.
 *
 * Safety rules:
 * - Snapshots older than 24h are treated as stale and ignored (a "live"
 *   match from days ago is not live anymore).
 * - Corrupt or wrongly-shaped data is ignored, never thrown.
 * - The snapshot is cleared whenever a match definitively ends
 *   (resetGame, summary save/skip, logout) so /manage never resurrects
 *   a finished match.
 */

export const LIVE_MATCH_PLAYERS_KEY = 'live_match_players_v1';
export const LIVE_MATCH_GAME_KEY = 'live_match_game_v1';
export const LIVE_MATCH_KEYS = [LIVE_MATCH_PLAYERS_KEY, LIVE_MATCH_GAME_KEY];

// A snapshot older than this is stale and will not be restored.
const MAX_SNAPSHOT_AGE_MS = 24 * 60 * 60 * 1000;

function safeParse(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isFresh(envelope) {
  return (
    envelope &&
    typeof envelope === 'object' &&
    typeof envelope.savedAt === 'number' &&
    Date.now() - envelope.savedAt < MAX_SNAPSHOT_AGE_MS
  );
}

function normalizePlayer(player) {
  if (!player || typeof player !== 'object' || typeof player.name !== 'string') {
    return null;
  }
  return {
    ...player,
    playIntervals: Array.isArray(player.playIntervals) ? player.playIntervals : [],
  };
}

/**
 * Load persisted players, or null when there is nothing usable.
 * Returned players are normalized so downstream code (validators,
 * getTotalPlayTime) always sees the shape it expects.
 */
export function loadLiveMatchPlayers() {
  const envelope = safeParse(localStorage.getItem(LIVE_MATCH_PLAYERS_KEY));
  if (!isFresh(envelope) || !Array.isArray(envelope.data)) return null;
  const players = envelope.data.map(normalizePlayer).filter(Boolean);
  return players;
}

export function saveLiveMatchPlayers(players) {
  try {
    if (!Array.isArray(players)) return;
    localStorage.setItem(
      LIVE_MATCH_PLAYERS_KEY,
      JSON.stringify({ savedAt: Date.now(), data: players })
    );
  } catch {
    // Storage full / private mode — the match still works in memory.
  }
}

const DEFAULT_GAME_SNAPSHOT = {
  isRunning: false,
  gameIntervals: [],
  ourScore: 0,
  opponentScore: 0,
  goals: [],
  goalkeeper: null,
  includeGKPlaytime: true,
};

/**
 * Load persisted game state, or null when there is nothing usable.
 */
export function loadLiveMatchGame() {
  const envelope = safeParse(localStorage.getItem(LIVE_MATCH_GAME_KEY));
  if (!isFresh(envelope) || !envelope.data || typeof envelope.data !== 'object') {
    return null;
  }
  const data = envelope.data;
  return {
    ...DEFAULT_GAME_SNAPSHOT,
    isRunning: data.isRunning === true,
    gameIntervals: Array.isArray(data.gameIntervals) ? data.gameIntervals : [],
    ourScore: typeof data.ourScore === 'number' ? data.ourScore : 0,
    opponentScore: typeof data.opponentScore === 'number' ? data.opponentScore : 0,
    goals: Array.isArray(data.goals) ? data.goals : [],
    goalkeeper: data.goalkeeper ?? null,
    includeGKPlaytime: data.includeGKPlaytime !== false,
  };
}

export function saveLiveMatchGame(game) {
  try {
    if (!game || typeof game !== 'object') return;
    localStorage.setItem(
      LIVE_MATCH_GAME_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        data: {
          isRunning: game.isRunning === true,
          gameIntervals: Array.isArray(game.gameIntervals) ? game.gameIntervals : [],
          ourScore: game.ourScore ?? 0,
          opponentScore: game.opponentScore ?? 0,
          goals: Array.isArray(game.goals) ? game.goals : [],
          goalkeeper: game.goalkeeper ?? null,
          includeGKPlaytime: game.includeGKPlaytime !== false,
        },
      })
    );
  } catch {
    // Storage full / private mode — the match still works in memory.
  }
}

/** Remove any live-match snapshot (match over, logged out, fresh start). */
export function clearLiveMatch() {
  try {
    LIVE_MATCH_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore — nothing to clear or storage unavailable.
  }
}
